import React from 'react';
import { useReactToPrint } from 'react-to-print';
import dayjs, { Dayjs } from 'dayjs';
import { css } from '@emotion/react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { color } from '../../../../styles';
import { DateSelector } from '../../../../components/mui/dateSelector';
import { OutlineButton } from '../../../../components/outlineButton';
import { useLoading } from '../../../../contexts/LoadingContext';
import { useFetchResident } from '../../../../hooks/api/resident/useFetchResident';
import { currentNursingHomeSelector } from '../../../../redux/config/currentNursingHome';
import { HelpTooltip } from './caption';
import html2canvas from 'html2canvas';
import { useSnackbar, SnackbarType } from '@/contexts/SnackbarContext';
import { jsPDF } from 'jspdf';
import { useFetchUrineGraphData, UrineGraphFetchFailedReason } from './useFetchUrineGraphData';
import { RawGraphHintTooltip } from '@/pages/residents/dfreeGraphList/rawGraphHintTooltip';
import { UrineTrendReportContent } from './urineTrendReportContent';
import { useUrineTrendPrintStyles } from './useUrineTrendPrintStyles';
import { PRINT_CONTENT_SCALE } from './printChartOptions';
import Highcharts from 'highcharts';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface Props {
  nursingHomeId: string;
  residentId: string;
}

interface ExportConfig {
  reportRef: React.RefObject<HTMLDivElement>;
  heatmapRef: React.RefObject<HTMLDivElement>;
  urineGraphRef: React.RefObject<HTMLDivElement>;
  lowerContainerRef: React.RefObject<HTMLDivElement>;
  reportHeaderRef: React.RefObject<HTMLDivElement>;
  showActivityStatus: boolean;
  onLoadingChange: (isLoading: boolean) => void;
}

const FlexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScreenReportContainer = css`
  padding-bottom: 24px;
`;

const HiddenPrintRoot = css`
  position: fixed;
  left: -9999px;
  top: 0;
  width: 202mm;
  z-index: -1;
  visibility: visible;
  opacity: 1;
  pointer-events: none;
  overflow: visible;
  background: #fff;
`;

const HiddenPrintContent = css`
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 24px;

  & [data-highcharts-chart] {
    /*
     * Highchartsを広い仮想キャンバスで描画してから縮小する。
     * 125% × 0.8 = 100%のため、印刷上の横幅はA4いっぱいを維持する。
     */
    width: ${100 / PRINT_CONTENT_SCALE}%;
    zoom: ${PRINT_CONTENT_SCALE};
  }
`;

const DateSelectContainer = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DataSelector = css`
  margin: 6px 0;
  display: grid;
  grid-template-columns: auto 1fr; /* 最大内容幅に基づいて列幅を設定 */
  align-items: center;
  gap: 16px;
`;

const UpperContainer = css`
  padding: 24px;
  border-bottom: 1px solid ${color.border_gray};
`;

const RawGraphChexkbox = css`
  display: flex;
  justify-content: end;
  padding: 12px 24px 12px 24px;
`;

const LabelText = css`
  text-align: end;
  min-width: 72px;
`;

const RawGraphLabel = css`
  display: inline-flex;
  align-items: center;
  gap: 0 2px;
`;

const ContentStyle = css`
  display: flex;
  justify-content: space-between;
  flex: 1;
  margin-left: 16px;
`;

// テスト印刷用。display: none; だとキャプチャ時に問題があるため画面外に配置
const PrintOnly = css`
  position: absolute;
  top: 0;
  left: -9999px;
  width: 1280px;
  background: #fff;
  z-index: -1;
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: none;
`;

const FormControlStyle = css`
  padding: 0;
  min-width: 80px;
`;

const SelectMarginTop = css`
  margin-top: 6px;
`;

const FormWithTooltip = css`
  display: flex;
  align-items: center;

  & > *:last-child {
    margin-right: 12px;
  }
`;

const NonRightMargin = css`
  margin-right: 0;
`;

const UpdateButton = css`
  border-radius: 24px;
  padding: 8px 24px;
`;

export const ResidentDetailUrineTrendAnalysis: React.FC<Props> = React.memo(({ residentId }) => {
  const { t, i18n } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { resident } = useFetchResident(true, +residentId);
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const currentLanguage = i18n.language;
  const printRef = React.useRef<HTMLDivElement>(null);

  // TODO: テスト用のref。あとで整理する
  const reportRef = React.useRef<HTMLDivElement>(null);
  const heatmapRef = React.useRef<HTMLDivElement>(null);
  const urineGraphRef = React.useRef<HTMLDivElement>(null);
  const lowerContainerRef = React.useRef<HTMLDivElement>(null);
  const reportHeaderRef = React.useRef<HTMLDivElement>(null);

  // 自由設定フォームの時間帯プルダウンデータ
  const times = Array.from({ length: 24 }, (_, i) => `${i}:00`);
  // 日付指定フォームの日時
  const [startDate, setStartDate] = React.useState<Dayjs>(dayjs().subtract(2, 'week').startOf('day').utc());
  const [endDate, setEndDate] = React.useState<Dayjs>(dayjs().startOf('day').utc());
  // 時間帯ラジオボタンの値（timeRangeFormArray参照）
  const [timeRange, setTimeRange] = React.useState<string>('all-time');
  // 時間帯の自由設定フォームの時間
  const [selectedStartTime, setSelectedStartTime] = React.useState<string>('7:00');
  const [selectedEndTime, setSelectedEndTime] = React.useState<string>('19:00');
  // 閾値ラジオボタンの値
  const [threshold, setThreshold] = React.useState<string>('6');
  // グラフの表示フラグ
  const [showThresholdMap, setShowThresholdMap] = React.useState<boolean>(true);
  const [showAverageMap, setShowAverageMap] = React.useState<boolean>(true);
  const [showNotificationPlot, setShowNotificationPlot] = React.useState<boolean>(true);
  const [showCareRecordPlot, setShowCareRecordPlot] = React.useState<boolean>(true);
  const [showSensorResample, setShowSensorResample] = React.useState<boolean>(false);
  const [showActivityStatus, setShowActivityStatus] = React.useState<boolean>(true);

  // メモフォームの設定
  const [memoText, setMemoText] = React.useState<string>('');
  const [showMemoBox, setShowMemoBox] = React.useState<boolean>(true);

  const [isPreparingPrint, setIsPreparingPrint] = React.useState(false);
  useUrineTrendPrintStyles();

  const { urineReportData, fetchUrineGraphData, updateThresholdRelatedData } = useFetchUrineGraphData();

  const timeRangeFormArray = [
    { key: 'all-time', value: t('allday', '終日') },
    { key: 'day-time', value: t('daytime7-19', '日中(7時~19時)') },
    { key: 'night-time', value: t('nighttime19-7', '夜間(19時~7時)') },
    { key: 'selected-time', value: t('specify', '自由設定') },
  ];

  const fetchFailedSnackBars: Record<UrineGraphFetchFailedReason, { text: string; type?: SnackbarType }> = {
    TOO_LONG_PERIOD: {
      text: t(
        'SpecifiedPeriodIsTooLong-PleaseNarrowDownThePeriodAndTryAgain',
        '選択期間が長すぎます。期間を絞ってやり直してください。',
      ),
      type: 'error',
    },
    NO_DATA: {
      text: t('dataForTheSpecifiedPeriodWasNotFound', '指定した期間のデータが見つかりませんでした'),
    },
    UNKNOWN_ERROR: {
      text: t('AnErrorOccurred', 'エラーが発生しました'),
      type: 'error',
    },
  };

  React.useEffect(() => {
    void handleUpdateButtonClick();
  }, []);

  const handleChangeStartDate = React.useCallback(
    (start: dayjs.Dayjs) => {
      setStartDate(start);
    },
    [startDate],
  );

  const handleChangeEndDate = React.useCallback(
    (end: dayjs.Dayjs) => {
      setEndDate(end);
    },
    [endDate],
  );

  const handleUpdateButtonClick = React.useCallback(async () => {
    const showFailedReason = (reason: UrineGraphFetchFailedReason) => {
      const config = fetchFailedSnackBars[reason];

      config.type ? showSnackbar(config.text, config.type) : showSnackbar(config.text);
    };

    showLoading();
    const result = await fetchUrineGraphData({
      start: startDate,
      end: endDate,
      residentId,
      timeRange,
      selectedStartTime,
      selectedEndTime,
      setThreshold,
    });

    hideLoading();

    if (!result.success) {
      showFailedReason(result.reason);
    }
  }, [startDate, endDate, timeRange, selectedStartTime, selectedEndTime]);

  const setThresholdRelatedData = (threshold: string) => {
    setThreshold(threshold);

    updateThresholdRelatedData(threshold);
  };

  const PrintButton = ({ disable }: { disable: boolean }) => {
    return (
      <OutlineButton
        disable={disable}
        onClick={handlePrint}
        icon={<img src='/images/icons/print.svg' alt={t('analysis', '履歴・分析')} />}
      >
        <span>{t('Print', '印刷')}</span>
      </OutlineButton>
    );
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      setIsPreparingPrint(true);
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
          const printCharts = Highcharts.charts.filter(
            (chart): chart is Highcharts.Chart =>
              !!chart && !!printRef.current?.contains(chart.container),
          );
          printCharts.forEach((chart) => chart.reflow());

          // reflowは内部で遅延実行されるため、再描画とブラウザの描画完了を待ってから印刷する
          setTimeout(() => {
            printCharts.forEach((chart) => chart.redraw(false));
            requestAnimationFrame(() => {
              requestAnimationFrame(() => resolve());
            });
          }, 500);
        }, 1500);
      });
    },
    onAfterPrint: () => {
      setIsPreparingPrint(false);
    },
    onPrintError: () => {
      setIsPreparingPrint(false);
    },
  });

  const GraphSubtitle = React.useMemo(() => {
    const formatString = currentLanguage === 'ja' ? 'YYYY/MM/DD' : 'MM/DD/YYYY';
    return (
      <span css={FlexCenter}>
        ({startDate.local().format(formatString) + ' ~ ' + endDate.local().format(formatString)})
      </span>
    );
  }, [startDate, endDate, currentLanguage]);

  const sensorReactionEntries = React.useMemo(
    () => Object.entries(urineReportData?.sensorReactionArray ?? {}),
    [urineReportData?.sensorReactionArray],
  );

  // 日別排尿グラフの最新の60日分を取り出し、それを昇順に並び替える
  const latestSensorReactionEntries = React.useMemo(
    () =>
      [...sensorReactionEntries]
        .sort((a, b) => dayjs(b[0]).valueOf() - dayjs(a[0]).valueOf())
        .slice(0, 60)
        .reverse(),
    [sensorReactionEntries],
  );

  const reportContentProps = {
    urineReportData,
    resident,
    currentNursingHome,
    startDate,
    endDate,
    memoText,
    showMemoBox,
    showThresholdMap,
    showAverageMap,
    showCareRecordPlot,
    showNotificationPlot,
    showSensorResample,
    showActivityStatus,
    graphSubtitle: GraphSubtitle,
    latestSensorReactionEntries,
    sensorReactionEntryCount: sensorReactionEntries.length,
  };

  const rawGraphCheckbox = (
    <div css={RawGraphChexkbox}>
      <FormControlLabel
        checked={showSensorResample}
        onChange={(e: any) => setShowSensorResample(e.target.checked)}
        control={<Checkbox />}
        label={
          <span css={RawGraphLabel}>
            {t('rawGraphWithOutCorrection', '補正前グラフ')}
            <RawGraphHintTooltip size={22} />
          </span>
        }
      />
    </div>
  );

  return (
    <>
      <div css={UpperContainer}>
        <div css={DataSelector}>
          <div css={LabelText}>{t('period', '期間')}:</div>
          <div css={ContentStyle}>
            <div css={DateSelectContainer}>
              <DateSelector newValue={startDate} maxDate={endDate.local()} onChange={handleChangeStartDate} />
              〜
              <DateSelector
                newValue={endDate}
                minDate={startDate.local()}
                maxDate={dayjs(new Date()).local()}
                onChange={handleChangeEndDate}
              />
            </div>
            <div css={[FlexCenter, DateSelectContainer]}>
              <PrintButton disable={!urineReportData?.hasGraphData.heatmap} />
              {process.env.REACT_APP_ENV_NAME !== 'production' && (
                <Button
                  onClick={() =>
                    handleExport({
                      reportRef,
                      heatmapRef,
                      urineGraphRef,
                      lowerContainerRef,
                      reportHeaderRef,
                      showActivityStatus,
                      onLoadingChange: (isLoading) => (isLoading ? showLoading() : hideLoading()),
                    })
                  }
                >
                  テスト印刷
                </Button>
              )}
              <Button css={UpdateButton} variant='contained' onClick={handleUpdateButtonClick}>
                {t('display', '表示する')}
              </Button>
            </div>
          </div>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('timePeriod', '時間帯')}:</div>
          <div>
            <div css={DateSelectContainer}>
              <FormControl component='fieldset'>
                <RadioGroup
                  row={true}
                  aria-label='group1'
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  {timeRangeFormArray.map((v, index) => (
                    <FormControlLabel key={index} value={v.key} control={<Radio />} label={v.value} />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl disabled={timeRange !== 'selected-time'} css={FormControlStyle}>
                <InputLabel>{t('startTime', '開始時間')}</InputLabel>
                <Select
                  css={SelectMarginTop}
                  variant='standard'
                  value={selectedStartTime}
                  onChange={(e) => setSelectedStartTime(e.target.value)}
                >
                  {times.map((time, i) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              〜
              <FormControl disabled={timeRange !== 'selected-time'} css={FormControlStyle}>
                <InputLabel>{t('endTime', '終了時間')}</InputLabel>
                <Select
                  css={SelectMarginTop}
                  variant='standard'
                  value={selectedEndTime}
                  onChange={(e) => setSelectedEndTime(e.target.value)}
                >
                  {times.map((time, i) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('aggregateColumn', '集計列')}:</div>
          <FormGroup row={true}>
            <div css={FormWithTooltip}>
              <FormControlLabel
                css={NonRightMargin}
                checked={showThresholdMap}
                onChange={(event, checked) => {
                  setShowThresholdMap(checked);
                }}
                control={<Checkbox />}
                label={t('thresholdValue', '閾値')}
              />
              <HelpTooltip
                title={t('borderlineToCalculateUrineTrend', '尿のたまり具合傾向値を算出する際のボーダーライン')}
                description={
                  currentLanguage === 'ja'
                    ? '集計期間全体でどのくらい尿がたまっている傾向にあるのか？の基準となる数値で、' +
                      '設定した閾値を超えた割合が傾向値に反映される'
                    : ''
                }
              />
            </div>
            <FormControlLabel
              checked={showAverageMap}
              onChange={(event, checked) => {
                setShowAverageMap(checked);
              }}
              control={<Checkbox />}
              label={t('average', '平均')}
            />
          </FormGroup>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('thresholdValue', '閾値')}:</div>
          <FormControl component='fieldset'>
            <RadioGroup
              row={true}
              aria-label='group2'
              value={threshold}
              onChange={(e) => setThresholdRelatedData(e.target.value)}
            >
              {Array.from({ length: 11 }).map((_, index) => (
                <FormControlLabel key={index} value={index} control={<Radio />} label={index} />
              ))}
            </RadioGroup>
          </FormControl>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('options', 'オプション')}:</div>
          <FormGroup row={true}>
            <FormControlLabel
              checked={showCareRecordPlot}
              onChange={(e: any) => setShowCareRecordPlot(e.target.checked)}
              control={<Checkbox />}
              label={t('careHistory', 'ケア履歴')}
            />
            <FormControlLabel
              checked={showNotificationPlot}
              onChange={(e: any) => setShowNotificationPlot(e.target.checked)}
              control={<Checkbox />}
              label={t('notification', '通知')}
            />

            <FormControlLabel
              checked={showActivityStatus}
              onChange={(e: any) => setShowActivityStatus(e.target.checked)}
              control={<Checkbox />}
              label={t('activityTrendDisplay', '活動傾向表示')}
            />
            <FormControlLabel
              checked={showMemoBox}
              onChange={(e: any) => setShowMemoBox(e.target.checked)}
              control={<Checkbox />}
              label={t('addMemo', 'メモ追加')}
            />
          </FormGroup>
        </div>
      </div>
      <div css={ScreenReportContainer}>
        <UrineTrendReportContent
          {...reportContentProps}
          isPrinting={false}
          useStaticMemo={false}
          onMemoChange={setMemoText}
          renderBeforeUrineGraphs={rawGraphCheckbox}
          refs={{
            heatmapRef,
            urineGraphRef,
            lowerContainerRef,
            reportHeaderRef,
          }}
        />
      </div>
      {isPreparingPrint && (
        <div css={HiddenPrintRoot} ref={printRef} className='urine-trend-print-root'>
          <div css={HiddenPrintContent} className='urine-trend-print-content'>
            <UrineTrendReportContent {...reportContentProps} isPrinting={true} useStaticMemo={true} />
          </div>
        </div>
      )}
      <div ref={reportRef} css={PrintOnly}>
        <div className='graph-container' />
        <div className='urine-graph-container' />
        <div className='care-record-container' />
      </div>
    </>
  );
});

// 共通のキャプチャ処理を行う関数
const captureElement = async (
  element: HTMLElement,
  options: {
    scale?: number;
    useCORS?: boolean;
    backgroundColor?: string;
    windowWidth?: number;
  } = {},
) => {
  const defaultOptions = {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: 1200,
  };

  const canvas = await html2canvas(element, { ...defaultOptions, ...options });
  return {
    canvas,
    imgData: canvas.toDataURL('image/png'),
  };
};

// 画像をPDFに追加する共通処理
const addImageToPdf = (
  pdf: jsPDF,
  imgData: string,
  headerHeight: number,
  options: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    center?: boolean;
  } = {},
) => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const props = pdf.getImageProperties(imgData);
  const aspectRatio = props.height / props.width;

  let renderWidth = options.width || pdfWidth;
  let renderHeight = renderWidth * aspectRatio;

  // 高さがページに収まらない場合の調整
  const maxHeight = pdfHeight - headerHeight - 20;
  if (renderHeight > maxHeight) {
    renderHeight = maxHeight;
    renderWidth = renderHeight / aspectRatio;
  }

  const x = options.center ? (pdfWidth - renderWidth) / 2 : options.x || 0;
  const y = options.y || headerHeight + 10;

  pdf.addImage(imgData, 'PNG', x, y, renderWidth, renderHeight);

  return { renderWidth, renderHeight };
};

// コンテナに要素を追加してキャプチャする共通処理
const captureContainerContent = async (
  container: HTMLElement,
  content: Node,
  options: {
    scale?: number;
    useCORS?: boolean;
    backgroundColor?: string;
    windowWidth?: number;
  } = {},
) => {
  container.innerHTML = '';
  const clonedContent = content.cloneNode(true);
  container.appendChild(clonedContent);

  // 描画が DOM に反映されるのを待つ
  await new Promise((res) => requestAnimationFrame(res));

  return await captureElement(container, options);
};

const handleExport = async ({
  reportRef,
  heatmapRef,
  urineGraphRef,
  lowerContainerRef,
  reportHeaderRef,
  showActivityStatus,
  onLoadingChange,
}: ExportConfig) => {
  if (!reportRef.current || !heatmapRef.current || !urineGraphRef.current || !reportHeaderRef.current) {
    console.error('いずれかのrefがnullです');
    return;
  }
  onLoadingChange(true);

  try {
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      compress: true,
    });

    // 日本語フォントを設定
    pdf.setFont('NotoSansJP');

    const pdfWidth = pdf.internal.pageSize.getWidth();

    // ReportHeaderのキャプチャを一度だけ行う
    const { imgData: headerImgData } = await captureElement(reportHeaderRef.current);
    const headerProps = pdf.getImageProperties(headerImgData);
    const headerAspectRatio = headerProps.height / headerProps.width;
    const headerHeight = pdfWidth * headerAspectRatio;

    // ヒートマップページの生成
    const heatmapTarget = reportRef.current.querySelector('.graph-container');
    if (!heatmapTarget) {
      console.error('graph-container が見つかりません');
      return;
    }
    const { imgData: heatmapImgData } = await captureContainerContent(heatmapTarget as HTMLElement, heatmapRef.current);

    pdf.addImage(headerImgData, 'PNG', 0, 0, pdfWidth, headerHeight);
    addImageToPdf(pdf, heatmapImgData, headerHeight, { center: true });

    // 日別尿量グラフの処理
    const urineGraphs = urineGraphRef.current.children;
    const graphsPerDay = showActivityStatus ? 2 : 1;
    const daysPerPage = showActivityStatus ? 6 : 9;
    const urineGraphsPerPage = daysPerPage * graphsPerDay;

    for (let i = 0; i < urineGraphs.length; i += urineGraphsPerPage) {
      pdf.addPage();

      // ReportHeaderを追加
      pdf.addImage(headerImgData, 'PNG', 0, 0, pdfWidth, headerHeight);

      const urineGraphTarget = reportRef.current.querySelector('.urine-graph-container');
      if (!urineGraphTarget) {
        console.error('urine-graph-container が見つかりません');
        return;
      }

      const pageGraphs = Array.from(urineGraphs).slice(i, i + urineGraphsPerPage);
      const tempContainer = document.createElement('div');
      pageGraphs.forEach((graph) => {
        const clonedGraph = graph.cloneNode(true);
        tempContainer.appendChild(clonedGraph);
      });

      const { imgData: graphImgData } = await captureContainerContent(urineGraphTarget as HTMLElement, tempContainer);

      addImageToPdf(pdf, graphImgData, headerHeight, { center: true });
    }

    // ケア介入回数と排尿状況のグラフを追加
    if (lowerContainerRef.current) {
      const careRecordTarget = reportRef.current.querySelector('.care-record-container');
      if (!careRecordTarget) {
        console.error('care-record-container が見つかりません');
        return;
      }

      const { imgData: lowerImgData } = await captureContainerContent(
        careRecordTarget as HTMLElement,
        lowerContainerRef.current,
      );

      pdf.addPage();
      pdf.addImage(headerImgData, 'PNG', 0, 0, pdfWidth, headerHeight);
      addImageToPdf(pdf, lowerImgData, headerHeight, { center: true });

      careRecordTarget.innerHTML = '';
    }

    pdf.save('heatmap-report.pdf');
  } catch (error) {
    console.error('PDF生成中にエラーが発生しました:', error);
  } finally {
    onLoadingChange(false);
  }
};
